import { useMutation } from '@apollo/client'
import { type GoalConfigFieldsFragment, UpdateGoalConfigDocument } from '@salescore/client-api'
import { getOrganizationIdFromPath, useCan } from '@salescore/client-common'
import { NotionLikeSelector } from '@salescore/frontend-common'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, startTransition, useContext } from 'react'
import { useRecoilValue } from 'recoil'

import { kpiParameterAtom } from '../../../recoil/navigation/atoms'
import { useSetGoalConfig } from '../../../recoil/view/atoms'
import { ViewRedirectContext } from '../../../ViewRedirectContext'

export const GoalConfigsPicker = ({
  canUpdate,
  viewId,
  goalConfigs,
  refetchGoalConfigs,
}: {
  canUpdate: boolean
  viewId: string
  goalConfigs: GoalConfigFieldsFragment[]
  refetchGoalConfigs: () => void
}): ReactElement => {
  const { goalConfig } = useRecoilValue(kpiParameterAtom)

  const setGoalConfig = useSetGoalConfig()
  const { redirectToGoalConfigs, redirectToGoalConfig } = useContext(ViewRedirectContext)
  const [update] = useMutation(UpdateGoalConfigDocument)
  const canManageGoal = useCan('manage-goal')

  return (
    <NotionLikeSelector
      disableAddition={!canManageGoal}
      showAddition={true}
      dropdownStyle={{ width: 350 }}
      className="mr-2"
      buttonProps={{ type: 'text' }}
      value={goalConfig?.id}
      options={[
        ...(goalConfigs.length > 0
          ? [
              {
                value: 'none',
                label: t('目標なし'),
                redirectToGoalConfig: undefined,
                disableDrag: true,
              },
            ]
          : []),
        ...goalConfigs.map((x) => ({
          value: x.id,
          label: x.name,
          redirectToGoalConfig: canManageGoal
            ? (): void => {
                redirectToGoalConfig(x.id)
              }
            : undefined,
          disableDrag: false,
        })),
      ]}
      setValue={(value) => {
        const newGoalConfig = goalConfigs.find((x) => x.id === value)
        setGoalConfig(newGoalConfig)
      }}
      placeholder={t(`目標なし`)}
      additionText={t(`目標を新規作成`)}
      onAddButtonClick={() => {
        redirectToGoalConfigs(viewId)
      }}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSort={
        // eslint-disable-next-line @typescript-eslint/strict-void-return
        canUpdate
          ? async (values: string[]): Promise<void> => {
              // eslint-disable-next-line sonarjs/todo-tag
              // TODO: この形でやるなら、recoilにgoalConfigsをいれたい
              const sortedGoalConfigs = values
                .map((value) => goalConfigs.find((x) => x.id === value))
                .filter(isNotNullish)
              if (sortedGoalConfigs.length === 0) {
                return
              }
              const promises = sortedGoalConfigs.map(
                async (config, rank) =>
                  await update({
                    variables: {
                      organizationId: getOrganizationIdFromPath(),
                      goalConfig: {
                        id: config.id,
                        name: config.name,
                        userGroupIdsConfig: config.userGroupIdsConfig,
                        userIdsConfig: config.userIdsConfig,
                        rank,
                      },
                    },
                  }),
              )
              await Promise.all(promises)
              startTransition(refetchGoalConfigs)
            }
          : undefined
      }
    />
  )
}
